import {
  PUBLISH_PROGRESS_DRAWER_Z,
  TOP_LAYER_VARS,
} from '@src/components/common/topLayerZ';

import type { CSSProperties } from 'react';

/**
 * Default tooltip style — used everywhere outside modals.
 *
 * Matches the Figma UI Kit tooltip design.
 *
 * Figma specs:
 *  - Background: var(--popover) — Gray-500
 *  - Text: var(--foreground) — Gray-100
 *  - Font size: var(--text-sm) — 14px
 *  - Font weight: var(--font-weight-medium) — 500
 *  - Border: 1px solid var(--gray-600)
 *  - Border radius: var(--radius)
 *  - Box shadow: var(--elevation-md)
 *  - Padding: 8px 12px
 */
export const TOOLTIP_STYLE: CSSProperties = {
  padding: '8px 12px',
  fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-weight-medium)',
  lineHeight: '120%',
  color: 'var(--foreground)',
  background: 'var(--popover)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--gray-600)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--elevation-md)',
  zIndex: `var(${TOP_LAYER_VARS.tooltip}, 100005)`,
};

/**
 * Compact variant — used by the sidebar (Home, Story Manager, Brand Kit,
 * the open/close toggle, avatar hover, current-story name, etc.).
 * Matches the visual look of the `SidebarTooltip` styled component so all
 * sidebar tooltips read as one consistent family. Use this whenever you
 * need a smaller tooltip (single-line short labels) instead of the
 * default `TOOLTIP_STYLE`.
 *
 * Specs:
 *  - Background: var(--popover)
 *  - Text: var(--popover-foreground)
 *  - Font size: 12px
 *  - Font weight: 500
 *  - Border: 1px solid var(--border-subtle)
 *  - Border radius: 6px
 *  - Box shadow: var(--elevation-sm)
 *  - Padding: 0 10px (height-locked, vertical centering via flex)
 *  - Height: 26px
 */
export const COMPACT_TOOLTIP_STYLE: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  height: '26px',
  boxSizing: 'border-box',
  padding: '0 10px',
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '16px',
  color: 'var(--popover-foreground)',
  background: 'var(--popover)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--border-subtle)',
  borderRadius: '6px',
  boxShadow: 'var(--elevation-sm)',
  whiteSpace: 'nowrap',
  zIndex: `var(${TOP_LAYER_VARS.tooltip}, 1000020)`,
};

/**
 * Publish-progress-drawer tooltip style — `COMPACT_TOOLTIP_STYLE` with a
 * resting z above `PUBLISH_PROGRESS_DRAWER_Z` (1000035).
 *
 * The drawer rests ABOVE the compact token's resting fallback (1000020), so a
 * tooltip anchored to a control inside the drawer would otherwise paint behind
 * the drawer's own panel and be invisible. Still reads the shared
 * `--top-layer-tooltip-z` var, so the Translation Review lift applies as usual.
 */
export const DRAWER_TOOLTIP_STYLE: CSSProperties = {
  ...COMPACT_TOOLTIP_STYLE,
  zIndex: `var(${TOP_LAYER_VARS.tooltip}, ${PUBLISH_PROGRESS_DRAWER_Z + 1})`,
};

/**
 * Compact single-line tooltip for controls INSIDE a modal — the Creator Studio
 * toolbar's compact look (26px, one line, never wraps) at the modal layer's
 * z-index. For icon-only controls and short "Go to …" hints in modal headers
 * (the Translation Review header, PLAT-11649); `MODAL_TOOLTIP_STYLE` stays the
 * choice for hints that may run to two lines.
 */
export const MODAL_COMPACT_TOOLTIP_STYLE: CSSProperties = {
  ...COMPACT_TOOLTIP_STYLE,
  whiteSpace: 'nowrap',
  zIndex: `var(${TOP_LAYER_VARS.tooltip}, 1000003)`,
};

/**
 * Modal-context tooltip style — for tooltips rendered inside modals.
 *
 * Differs from `TOOLTIP_STYLE` in two important ways:
 *  - Higher resting z-index (1000003) so the tooltip renders above the modal
 *    layer (raised further only while the clip-preview review lifts the
 *    `--top-layer-tooltip-z` var — see topLayerZ.ts).
 *  - Smaller / tighter visual (font size xs, padding 5px 10px) appropriate for
 *    dense modal-context UI.
 *
 * Use spread for one-off width tweaks, e.g.
 * `style={{ ...MODAL_TOOLTIP_STYLE, maxWidth: '420px' }}`.
 */
export const MODAL_TOOLTIP_STYLE: CSSProperties = {
  maxWidth: '240px',
  padding: '5px 10px',
  fontSize: 'var(--text-xs)',
  fontWeight: 'var(--font-weight-medium)',
  lineHeight: '1.4',
  color: 'var(--popover-foreground)',
  backgroundColor: 'var(--popover)',
  border: '1px solid var(--border-subtle)',
  borderRadius: '6px',
  boxShadow: 'var(--elevation-sm)',
  zIndex: `var(${TOP_LAYER_VARS.tooltip}, 1000003)`,
};

/**
 * Multi-line / paragraph tooltips — explanatory copy that runs to a few
 * sentences (e.g. a "what does this button do?" help icon), as opposed to the
 * short labels `TOOLTIP_STYLE` is tuned for.
 *
 * The default style (14px, weight 500, 1.2 line-height, centered) reads as a
 * dense block at paragraph length. This variant keeps the same default-tooltip
 * palette but is built for reading: regular weight, a looser 1.5 line-height,
 * left alignment, a touch more vertical padding, and a wider max-width. Use it
 * outside modals; inside modals keep `MODAL_TOOLTIP_STYLE`.
 */
export const PARAGRAPH_TOOLTIP_STYLE: CSSProperties = {
  maxWidth: '300px',
  padding: '10px 12px',
  fontSize: 'var(--text-xs)',
  fontWeight: 'var(--font-weight-normal)',
  lineHeight: '1.5',
  textAlign: 'left',
  color: 'var(--foreground)',
  background: 'var(--popover)',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'var(--gray-600)',
  borderRadius: 'var(--radius)',
  boxShadow: 'var(--elevation-md)',
  zIndex: 100005,
};
